<!--
  Orders (orders.vue)
  ==================
  Admin orders list and detail view. Fetches orders from the backend, supports
  search/filter, and allows processing, completing, or cancelling orders.

  Features:
  - List: table of orders with Order ID, Customer (name/phone), Contact, Location,
    Items, Amount, Status, Date & Time, and row actions (View, Process, Complete, Cancel).
  - Search: by customer name, phone, email, order number, city, or address (email not shown in UI).
  - Filter: by status (All, Pending, Processing, Completed, Cancelled).
  - Detail modal: full order info (customer, delivery, items, summary, PIN location),
    with status-based actions (Process → Complete/Cancel; Completed/Cancelled are read-only).
  - Cancel flow: confirmation modal before cancelling; toasts for all success/error feedback.
  - Responsive: horizontal scroll for table on small screens; fluid layout and breakpoints
    at 1024px, 768px, 480px, 360px.

  API (aligned with backend orders.py):
  - GET  /orders/                          → list orders (OrderGetSchema).
  - PATCH /orders/{order_number}/process   → set status to processing.
  - PATCH /orders/{order_number}/complete  → set status to completed (or .../status + body).
  - PATCH /orders/{order_number}/cancel    → set status to cancelled (or .../status + body).

  No props or emitted events. Fetches on mount.
-->
<template>
  <section class="orders-wrapper">

    <!-- ACCESS DENIED: admin must be super_admin or support -->
    <div v-if="!canManageOrders" class="access-denied-card">
      <div class="access-denied-icon"><i class="fa-solid fa-lock"></i></div>
      <h3>Access restricted</h3>
      <p>Only super admin or support can view and manage orders. Your role does not have permission.</p>
      <p class="access-denied-hint">Sign in with an account that has orders access.</p>
    </div>

    <!-- PAGE HEADER (only when allowed) -->
    <div v-else class="orders-header">
      <div class="header-left">
        <h2 class="title">Orders</h2>
        <p class="subtitle">{{ filteredOrders.length }} order{{ filteredOrders.length !== 1 ? 's' : '' }}</p>
      </div>

      <div class="actions">
        <div class="search-container">
          <i class="fa-solid fa-magnifying-glass search-icon"></i>
          <input
            type="text"
            v-model="search"
            class="search-box"
            placeholder="Search orders..."
          />
        </div>

        <div class="filter-container">
          <i class="fa-solid fa-filter filter-icon"></i>
          <select v-model="statusFilter" class="filter-select">
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>
    </div>

    <!-- EXPANDED TABLE -->
    <div v-if="canManageOrders && filteredOrders.length" class="table-container">
      <div class="table-scroll">
      <table class="expanded-table">
        <thead>
          <tr>
            <th>Order ID</th>
            <th>Customer</th>
            <th>Contact</th>
            <th>Location</th>
            <th>Items</th>
            <th>Amount</th>
            <th>Status</th>
            <th>Date & Time</th>
            <th>Actions</th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="order in filteredOrders"
            :key="order.order_number"
            class="table-row"
          >
            <td class="order-id">
              <div class="order-number">#{{ order.order_number }}</div>
            </td>

            <td class="customer-col">
              <div class="customer-name">{{ order.customer_name }}</div>
              <div class="customer-contact">{{ order.customer_phone }}</div>
            </td>

            <td class="contact-col">
              <div class="phone">
                <i class="fa-solid fa-phone contact-icon"></i>
                {{ order.customer_phone }}
              </div>
            </td>

            <td class="location-col">
              <div class="city">
                <i class="fa-solid fa-location-dot location-icon"></i>
                {{ order.delivery_city }}
              </div>
              <div class="area" v-if="order.delivery_address">
                {{ truncateAddress(order.delivery_address) }}
              </div>
            </td>

            <td class="items-col">
              <div class="items-count">{{ order.items?.length || 0 }} item{{ (order.items?.length || 0) !== 1 ? 's' : '' }}</div>
              <div class="items-total">Ksh {{ formatNumber(order.subtotal) }}</div>
              <div v-if="order.discount_savings > 0" class="discount-indicator">
                <i class="fa-solid fa-tag"></i> Discount applied
              </div>
            </td>

            <td class="amount-col">
              <div class="total-amount">Ksh {{ formatNumber(order.total_amount) }}</div>
              <div v-if="order.discount_savings > 0" class="savings-indicator">
                Saved Ksh {{ formatNumber(order.discount_savings) }}
              </div>
              <div class="delivery-fee" v-if="order.delivery_fee > 0">
                + Ksh {{ formatNumber(order.delivery_fee) }} delivery
              </div>
            </td>

            <td class="status-col">
              <div class="status-wrapper">
                <span class="status-badge" :class="order.status">
                  <i :class="statusIcon(order.status)"></i>
                  {{ formatStatusText(order.status) }}
                </span>
                <div class="status-time" v-if="order.status === 'processing'">
                  <i class="fa-solid fa-clock"></i>
                  In transit
                </div>
              </div>
            </td>

            <td class="datetime-col">
              <div class="date">{{ formatDate(order.created_at) }}</div>
              <div class="time">{{ formatTime(order.created_at) }}</div>
              <div class="time-ago">{{ getTimeAgo(order.created_at) }}</div>
            </td>

            <td class="actions-col">
              <div class="action-buttons">
                <button class="view-btn" @click="openModal(order)" title="View details">
                  <i class="fa-solid fa-eye"></i>
                  View
                </button>
                <div class="quick-actions">
                  <button 
                    v-if="order.status === 'pending'" 
                    class="quick-btn process" 
                    @click.stop="processOrder(order)"
                    title="Process order"
                  >
                    <i class="fa-solid fa-play"></i>
                  </button>
                  <button 
                    v-if="order.status === 'processing'" 
                    class="quick-btn complete" 
                    @click.stop="completeOrder(order)"
                    title="Mark as complete"
                  >
                    <i class="fa-solid fa-check"></i>
                  </button>
                  <button 
                    v-if="order.status !== 'cancelled' && order.status !== 'completed'" 
                    class="quick-btn cancel" 
                    @click.stop="cancelOrder(order)"
                    title="Cancel order"
                  >
                    <i class="fa-solid fa-xmark"></i>
                  </button>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      </div>
    </div>

    <!-- LOADING STATE -->
    <div v-else-if="canManageOrders && loading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading orders...</p>
    </div>

    <!-- EMPTY STATE -->
    <div v-else-if="canManageOrders" class="empty-state">
      <div class="empty-icon">
        <i class="fa-solid fa-clipboard-list"></i>
      </div>
      <h3>No orders found</h3>
      <p v-if="search || statusFilter">Try changing your search or filter</p>
      <p v-else>When customers place orders, they'll appear here</p>
    </div>

    <!-- Confirm Cancel Order Modal -->
    <div v-if="showConfirmCancel" class="modal-overlay" @click.self="cancelConfirmCancel">
      <div class="modal-box confirm-modal">
        <div class="modal-header">
          <h2><i class="fa-solid fa-triangle-exclamation"></i> Cancel order</h2>
          <button class="close-modal-btn" @click="cancelConfirmCancel"><i class="fa-solid fa-xmark"></i></button>
        </div>
        <div class="modal-body">
          <p>Are you sure you want to cancel order <strong>#{{ confirmCancelOrder?.order_number }}</strong>? This cannot be undone.</p>
          <div class="modal-actions-inline">
            <button type="button" class="view-btn" @click="cancelConfirmCancel">Keep order</button>
            <button type="button" class="quick-btn cancel cancel-confirm-btn" @click="confirmCancelOrderAction">
              <i class="fa-solid fa-xmark"></i> Cancel order
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ORDER DETAILS MODAL -->
    <transition name="fade">
      <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
        <transition name="slide-up">
          <div class="modal-box" :class="{ processing: activeOrder.status === 'processing' }">
            
            <!-- MODAL HEADER -->
            <div class="modal-header">
              <div class="modal-title-section">
                <h2>Order #{{ activeOrder.order_number }}</h2>
                <div class="order-meta">
                  <span class="meta-item">
                    <i class="fa-solid fa-calendar"></i>
                    {{ formatDate(activeOrder.created_at) }}
                  </span>
                  <span class="meta-item">
                    <i class="fa-solid fa-clock"></i>
                    {{ formatTime(activeOrder.created_at) }}
                  </span>
                </div>
              </div>
              <button class="close-modal-btn" @click="closeModal">
                <i class="fa-solid fa-xmark"></i>
              </button>
            </div>

            <!-- STATUS BANNER -->
            <div class="status-banner" :class="activeOrder.status">
              <div class="status-content">
                <i :class="statusIcon(activeOrder.status)"></i>
                <div>
                  <h3>{{ formatStatusText(activeOrder.status) }}</h3>
                  <p v-if="activeOrder.status === 'processing'">
                    Order is on the way to the customer
                  </p>
                  <p v-else-if="activeOrder.status === 'pending'">
                    Awaiting processing
                  </p>
                  <p v-else-if="activeOrder.status === 'completed'">
                    Delivered successfully
                  </p>
                  <p v-else-if="activeOrder.status === 'cancelled'">
                    Order was cancelled
                  </p>
                </div>
              </div>
            </div>

            <!-- TWO COLUMN LAYOUT -->
            <div class="modal-content">
              <!-- LEFT COLUMN: CUSTOMER & DELIVERY -->
              <div class="modal-left">
                <!-- CUSTOMER INFO -->
                <div class="info-card">
                  <h3 class="info-title">
                    <i class="fa-solid fa-user"></i>
                    Customer Information
                  </h3>
                  <div class="info-grid">
                    <div class="info-item">
                      <span class="info-label">Name</span>
                      <span class="info-value">{{ activeOrder.customer_name }}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Phone</span>
                      <span class="info-value">{{ activeOrder.customer_phone }}</span>
                    </div>
                    <div class="info-item full-width">
                      <span class="info-label">Delivery Address</span>
                      <span class="info-value">{{ activeOrder.delivery_address }}, {{ activeOrder.delivery_city }}</span>
                    </div>
                    <div class="info-item full-width" v-if="activeOrder.delivery_notes">
                      <span class="info-label">Delivery Notes</span>
                      <span class="info-value">{{ activeOrder.delivery_notes }}</span>
                    </div>
                    <div class="info-item full-width" v-if="activeOrder.pin_location && isValidGoogleMapsUrl(activeOrder.pin_location)">
                      <span class="info-label">
                        <i class="fa-solid fa-map-pin"></i>
                        PIN Location
                      </span>
                      <a 
                        :href="sanitizeUrl(activeOrder.pin_location)" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        class="pin-location-link"
                        @click.prevent="openPinLocation(activeOrder.pin_location)"
                      >
                        <i class="fa-solid fa-external-link"></i>
                        View on Google Maps
                      </a>
                    </div>
                    <div class="info-item full-width" v-else-if="activeOrder.pin_location">
                      <span class="info-label">
                        <i class="fa-solid fa-map-pin"></i>
                        PIN Location
                      </span>
                      <span class="info-value warning-text">
                        <i class="fa-solid fa-triangle-exclamation"></i>
                        Invalid or unsafe URL
                      </span>
                    </div>
                  </div>
                </div>

                <!-- ORDER SUMMARY -->
                <div class="info-card">
                  <h3 class="info-title">
                    <i class="fa-solid fa-receipt"></i>
                    Order Summary
                  </h3>
                  
                  <!-- Subtotal -->
                  <div class="summary-item">
                    <span>Subtotal ({{ activeOrder.items?.length || 0 }} items)</span>
                    <span>Ksh {{ formatNumber(activeOrder.subtotal) }}</span>
                  </div>
                  
                  <!-- Discount Savings (if any) -->
                  <div v-if="activeOrder.discount_savings > 0" class="summary-item discount">
                    <span>
                      <i class="fa-solid fa-tag"></i>
                      Discount Savings
                    </span>
                    <span class="discount-amount">- Ksh {{ formatNumber(activeOrder.discount_savings) }}</span>
                  </div>
                  
                  <!-- Total After Discount (if discount applied) -->
                  <div v-if="activeOrder.discount_savings > 0" class="summary-item after-discount">
                    <span>Total After Discount</span>
                    <span>Ksh {{ formatNumber(activeOrder.subtotal) }}</span>
                  </div>
                  
                  <!-- Delivery Fee -->
                  <div class="summary-item">
                    <span>Delivery Fee</span>
                    <span>Ksh {{ formatNumber(activeOrder.delivery_fee) }}</span>
                  </div>
                  
                  <!-- Grand Total -->
                  <div class="summary-total">
                    <span>Grand Total</span>
                    <strong>Ksh {{ formatNumber(activeOrder.total_amount) }}</strong>
                  </div>
                  
                  <!-- Payment Method -->
                  <div class="summary-item">
                    <span>Payment Method</span>
                    <span class="payment-method">
                      <i class="fa-solid fa-money-bill-wave"></i>
                      {{ activeOrder.payment_method || 'Cash on Delivery' }}
                    </span>
                  </div>
                  
                  <!-- Savings Message (if discount applied) -->
                  <div v-if="activeOrder.discount_savings > 0" class="savings-message">
                    <i class="fa-solid fa-money-bill-wave"></i>
                    You saved Ksh {{ formatNumber(activeOrder.discount_savings) }}
                  </div>
                </div>
              </div>

              <!-- RIGHT COLUMN: ORDER ITEMS -->
              <div class="modal-right">
                <div class="items-card">
                  <h3 class="info-title">
                    <i class="fa-solid fa-box"></i>
                    Order Items ({{ activeOrder.items?.length || 0 }})
                    <span v-if="activeOrder.discount_savings > 0" class="discount-badge-header">
                      <i class="fa-solid fa-percentage"></i>
                      Discount Applied
                    </span>
                  </h3>
                  
                  <div class="items-list">
                    <div
                      v-for="(item, itemIndex) in activeOrder.items"
                      :key="`${item.item_id}-${item.color_id || 'default'}-${itemIndex}`"
                      class="item-row"
                    >
                      <div class="item-thumb-wrap">
                        <img
                          :src="getItemImage(item)"
                          :alt="item.name"
                          class="item-thumb"
                          loading="lazy"
                          decoding="async"
                          @error="handleItemImageError"
                        />
                      </div>
                      <div class="item-info">
                        <div class="item-name">{{ item.name }}</div>
                        <div
                          v-if="item.color_label || item.color_id"
                          class="item-color-meta"
                        >
                          <span
                            v-if="item.color_primary || item.color_secondary"
                            class="item-color-swatch"
                            aria-hidden="true"
                          >
                            <span
                              class="item-color-swatch__half"
                              :style="{ background: '#' + (item.color_primary || 'ccc') }"
                            ></span>
                            <span
                              class="item-color-swatch__half"
                              :style="{ background: '#' + (item.color_secondary || item.color_primary || 'ccc') }"
                            ></span>
                          </span>
                          Color: {{ item.color_label || item.color_id }}
                        </div>
                        <div class="item-meta">
                          <span class="item-quantity">×{{ item.quantity }}</span>
                          <span v-if="formatOrderItemSizes(item)" class="item-size-line">
                            {{ formatOrderItemSizes(item) }}
                          </span>
                          
                          <!-- Show original and discounted price if item has discount -->
                          <div v-if="item.discounted && item.discount_percentage" class="price-details">
                            <div class="original-price">
                              @ Ksh {{ formatNumber(item.price) }}
                            </div>
                            <div class="discounted-price">
                              @ Ksh {{ formatNumber(item.final_price || item.price) }}
                              <span class="discount-badge-item">
                                {{ item.discount_percentage }}% OFF
                              </span>
                            </div>
                          </div>
                          
                          <!-- Regular price if no discount -->
                          <div v-else class="regular-price">
                            <span class="item-price">@ Ksh {{ formatNumber(item.price) }}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div class="item-total">
                        Ksh {{ formatNumber((item.final_price || item.price) * item.quantity) }}
                        
                        <!-- Show original total crossed out if discounted -->
                        <div v-if="item.discounted && item.discount_percentage" class="original-total">
                          <span class="original-total-crossed">
                            Ksh {{ formatNumber(item.price * item.quantity) }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Total Savings Summary -->
                  <div v-if="activeOrder.discount_savings > 0" class="savings-summary">
                    <div class="savings-row">
                      <span>Total Original Price:</span>
                      <span>Ksh {{ formatNumber(activeOrder.subtotal + activeOrder.discount_savings) }}</span>
                    </div>
                    <div class="savings-row discount">
                      <span>Total Discount:</span>
                      <span>- Ksh {{ formatNumber(activeOrder.discount_savings) }}</span>
                    </div>
                    <div class="savings-row final">
                      <span>Price After Discount:</span>
                      <span>Ksh {{ formatNumber(activeOrder.subtotal) }}</span>
                    </div>
                  </div>
                </div>

                <!-- ACTION BUTTONS -->
                <div class="action-buttons-card">
                  <div class="action-buttons-grid">
                    <!-- PENDING -->
                    <template v-if="activeOrder.status === 'pending'">
                      <button 
                        class="action-btn process" 
                        @click="processOrder(activeOrder)"
                        :disabled="processing"
                      >
                        <i class="fa-solid fa-play"></i>
                        {{ processing ? 'Processing...' : 'Start Processing' }}
                      </button>
                      <button 
                        class="action-btn cancel" 
                        @click="cancelOrder(activeOrder)"
                        :disabled="processing"
                      >
                        <i class="fa-solid fa-xmark"></i>
                        Cancel Order
                      </button>
                    </template>

                    <!-- PROCESSING -->
                    <template v-else-if="activeOrder.status === 'processing'">
                      <button 
                        class="action-btn complete" 
                        @click="completeOrder(activeOrder)"
                        :disabled="processing"
                      >
                        <i class="fa-solid fa-check"></i>
                        {{ processing ? 'Completing...' : 'Mark as Complete' }}
                      </button>
                      <button 
                        class="action-btn cancel" 
                        @click="cancelOrder(activeOrder)"
                        :disabled="processing"
                      >
                        <i class="fa-solid fa-xmark"></i>
                        Cancel Order
                      </button>
                    </template>

                    <!-- COMPLETED -->
                    <template v-else-if="activeOrder.status === 'completed'">
                      <button class="action-btn disabled" disabled>
                        <i class="fa-solid fa-check-circle"></i>
                        Order Completed
                      </button>
                      <button class="action-btn secondary" @click="closeModal">
                        <i class="fa-solid fa-arrow-left"></i>
                        Back to Orders
                      </button>
                    </template>

                    <!-- CANCELLED -->
                    <template v-else-if="activeOrder.status === 'cancelled'">
                      <button class="action-btn disabled" disabled>
                        <i class="fa-solid fa-ban"></i>
                        Order Cancelled
                      </button>
                      <button class="action-btn secondary" @click="closeModal">
                        <i class="fa-solid fa-arrow-left"></i>
                        Back to Orders
                      </button>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </transition>

  </section>
</template>

<script>
import { useAdminAuthStore } from "@/authen/adminAuth";
import globalToastMixin from "@/mixins/globalToastMixin";
import { buildApiUrl } from "@/config/api";
import { formatItemSizesSummary } from "@/utils/orderSizes";

const PLACEHOLDER_IMAGE = require("@/assets/ipalogo.png");

/**
 * Admin Orders component.
 * Displays a filterable, searchable list of orders and a detail modal with
 * status actions (process, complete, cancel). Uses toasts for feedback and
 * a confirmation modal for cancel. All API calls require admin Bearer token;
 * only super_admin or support can list/process/complete/cancel orders.
 * Data shape matches backend OrderGetSchema.
 */
export default {
  name: "CustomerOrders",
  mixins: [globalToastMixin],

  data() {
    return {
      search: "",
      statusFilter: "",
      loading: false,
      processing: false,
      orders: [],           // raw list from API (normalized in fetchOrders)
      showModal: false,
      activeOrder: {},      // copy of order shown in detail modal
      showConfirmCancel: false,
      confirmCancelOrder: null,
    };
  },

  computed: {
    /** True if admin is logged in and has super_admin or support role (can manage orders). */
    canManageOrders() {
      const store = useAdminAuthStore();
      return store.canManageOrders;
    },

    /** Orders filtered by search term (name, phone, email, order_number, city, address) and status. */
    filteredOrders() {
      return this.orders.filter((order) => {
        const searchTerm = this.search.toLowerCase();
        const matchesSearch = 
          order.customer_name?.toLowerCase().includes(searchTerm) ||
          order.customer_phone?.toLowerCase().includes(searchTerm) ||
          order.customer_email?.toLowerCase().includes(searchTerm) ||
          order.order_number?.toString().includes(searchTerm) ||
          order.delivery_city?.toLowerCase().includes(searchTerm) ||
          order.delivery_address?.toLowerCase().includes(searchTerm);

        const matchesStatus = !this.statusFilter || order.status === this.statusFilter;

        return matchesSearch && matchesStatus;
      });
    },
  },

  methods: {
    formatOrderItemSizes(item) {
      if (Array.isArray(item?.sizes) && item.sizes.length) {
        return formatItemSizesSummary({
          sub_category: item.sub_category,
          sizes: item.sizes,
          size: item.size,
        });
      }
      if (item?.size) {
        return `Size: ${item.size}`;
      }
      return "";
    },

    getItemImage(item) {
      if (item?.thumbnail) return item.thumbnail;
      return PLACEHOLDER_IMAGE;
    },

    handleItemImageError(event) {
      if (event?.target) event.target.src = PLACEHOLDER_IMAGE;
    },

    /** Returns headers for order API calls (admin Bearer token). Required for GET/PATCH order routes. */
    getOrderAuthHeaders() {
      const store = useAdminAuthStore();
      const token = store.accessToken;
      const headers = { "Content-Type": "application/json", "Accept": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;
      return headers;
    },

    /** Fetches orders from GET /orders/ and normalizes to frontend shape. Requires admin (super_admin or support). */
    async fetchOrders() {
      try {
        this.loading = true;
        const response = await fetch(buildApiUrl("/orders/"), {
          headers: this.getOrderAuthHeaders(),
        });

        if (response.status === 401) {
          this.showToastMessage("Sign in required", "Please sign in as an admin to view orders.", "error");
          this.orders = [];
          return;
        }
        if (response.status === 403) {
          this.showToastMessage("Access denied", "Only super admin or support can manage orders.", "error");
          this.orders = [];
          return;
        }
        if (!response.ok) {
          throw new Error(`Failed to fetch orders: ${response.status}`);
        }

        const ordersData = await response.json();
        
        // Transform backend data to match frontend expectations
        this.orders = ordersData.map(order => ({
          // Order identification
          order_number: order.order_number || order.id,
          
          // Customer information
          customer_name: order.name || order.customer_name,
          customer_phone: order.phone || order.customer_phone,
          customer_email: order.customer_email,
          customer_number: order.customer_number,
          
          // Delivery information
          delivery_address: order.address || order.delivery_address,
          delivery_city: order.city || order.delivery_city,
          delivery_notes: order.notes || order.delivery_notes,
          pin_location: order.pin_location || null,
          delivery_fee: parseFloat(order.delivery_fee) || 0,
          
          // Order items with discount information
          items: Array.isArray(order.items) ? order.items.map(item => ({
            item_id: item.item_id,
            name: item.name,
            quantity: item.quantity,
            price: parseFloat(item.price) || 0,
            final_price: parseFloat(item.final_price) || parseFloat(item.price) || 0,
            discount_percentage: parseFloat(item.discount_percentage) || null,
            discounted: item.discounted || false,
            sub_category: item.sub_category || null,
            size: item.size || null,
            sizes: Array.isArray(item.sizes) ? item.sizes : (item.size ? [item.size] : []),
            color_id: item.color_id || null,
            color_label: item.color_label || null,
            color_primary: item.color_primary
              ? String(item.color_primary).replace(/^#/, "").toLowerCase()
              : null,
            color_secondary: item.color_secondary
              ? String(item.color_secondary).replace(/^#/, "").toLowerCase()
              : null,
            thumbnail: item.thumbnail || null,
          })) : [],
          
          // Financial information with discount support
          total_amount: parseFloat(order.total_amount) || 0,
          subtotal: parseFloat(order.subtotal) || 
                   (parseFloat(order.total_amount || 0) - parseFloat(order.delivery_fee || 0)),
          discount_savings: parseFloat(order.discount_savings) || 0,
          currency: order.currency || "KES",
          payment_method: order.payment_method || "cash_on_delivery",
          
          // Status
          status: order.status?.toLowerCase() || "pending",
          
          // Timestamps
          created_at: order.created_at || new Date().toISOString(),
        }));
        
      } catch (error) {
        console.error("Error fetching orders:", error);
        this.showToastMessage("Error", "Failed to load orders", "error");
      } finally {
        this.loading = false;
      }
    },

    /** PATCH /orders/{order_number}/process; sets local status to "processing" and shows toast. Requires admin. */
    async processOrder(order) {
      try {
        this.processing = true;
        const response = await fetch(
          buildApiUrl(`/orders/${order.order_number}/process`),
          { method: "PATCH", headers: this.getOrderAuthHeaders() }
        );

        if (response.status === 401) {
          this.showToastMessage("Sign in required", "Please sign in as an admin to manage orders.", "error");
          return;
        }
        if (response.status === 403) {
          this.showToastMessage("Access denied", "Only super admin or support can process orders.", "error");
          return;
        }
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.detail || "Failed to process order");
        }
        
        // Update local state
        order.status = "processing";
        this.showToastMessage("Processing", `Order #${order.order_number} is now processing`, "success");
        
      } catch (error) {
        console.error("Error processing order:", error);
        this.showToastMessage("Error", error.message || "Failed to process order", "error");
      } finally {
        this.processing = false;
        this.closeModal();
      }
    },

    /** PATCH .../complete (or .../status); sets local status to "completed" and shows toast. Requires admin. */
    async completeOrder(order) {
      try {
        this.processing = true;
        const headers = this.getOrderAuthHeaders();
        let response = await fetch(
          buildApiUrl(`/orders/${order.order_number}/status`),
          { method: "PATCH", headers, body: JSON.stringify({ status: "completed" }) }
        );
        if (!response.ok && response.status !== 401 && response.status !== 403) {
          response = await fetch(
            buildApiUrl(`/orders/${order.order_number}/complete`),
            { method: "PATCH", headers }
          );
        }
        if (response.status === 401) {
          this.showToastMessage("Sign in required", "Please sign in as an admin to manage orders.", "error");
          return;
        }
        if (response.status === 403) {
          this.showToastMessage("Access denied", "Only super admin or support can complete orders.", "error");
          return;
        }
        if (!response.ok) {
          throw new Error("Failed to complete order");
        }
        
        order.status = "completed";
        this.showToastMessage("Completed", `Order #${order.order_number} completed`, "success");
        
      } catch (error) {
        console.error("Error completing order:", error);
        this.showToastMessage("Error", error.message || "Failed to complete order", "error");
      } finally {
        this.processing = false;
        this.closeModal();
      }
    },

    /** Opens the cancel-confirmation modal for the given order. */
    cancelOrder(order) {
      this.confirmCancelOrder = order;
      this.showConfirmCancel = true;
    },

    /** Closes the cancel-confirmation modal without cancelling. */
    cancelConfirmCancel() {
      this.showConfirmCancel = false;
      this.confirmCancelOrder = null;
    },

    /** Performs PATCH .../cancel (or .../status); updates local state and shows toast. Requires admin. */
    async confirmCancelOrderAction() {
      const order = this.confirmCancelOrder;
      if (!order) return;
      this.cancelConfirmCancel();
      try {
        this.processing = true;
        const headers = this.getOrderAuthHeaders();
        let response = await fetch(
          buildApiUrl(`/orders/${order.order_number}/status`),
          { method: "PATCH", headers, body: JSON.stringify({ status: "cancelled" }) }
        );
        if (!response.ok && response.status !== 401 && response.status !== 403) {
          response = await fetch(
            buildApiUrl(`/orders/${order.order_number}/cancel`),
            { method: "PATCH", headers }
          );
        }
        if (response.status === 401) {
          this.showToastMessage("Sign in required", "Please sign in as an admin to manage orders.", "error");
          return;
        }
        if (response.status === 403) {
          this.showToastMessage("Access denied", "Only super admin or support can cancel orders.", "error");
          return;
        }
        if (!response.ok) {
          throw new Error("Failed to cancel order");
        }
        order.status = "cancelled";
        this.showToastMessage("Cancelled", `Order #${order.order_number} cancelled`, "info");
      } catch (error) {
        console.error("Error cancelling order:", error);
        this.showToastMessage("Error", error.message || "Failed to cancel order", "error");
      } finally {
        this.processing = false;
        this.closeModal();
      }
    },

    /** Opens the order-detail modal with a copy of the order. */
    openModal(order) {
      this.activeOrder = { ...order };
      this.showModal = true;
    },

    /** Closes the order-detail modal (no-op while processing). */
    closeModal() {
      if (!this.processing) {
        this.showModal = false;
        this.activeOrder = {};
      }
    },

    /** Formats ISO date string as "Mon DD, YYYY". */
    formatDate(dateString) {
      if (!dateString) return "N/A";
      try {
        return new Date(dateString).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric"
        });
      } catch {
        return "Invalid Date";
      }
    },

    /** Formats ISO date string as time "HH:MM AM/PM". */
    formatTime(dateString) {
      if (!dateString) return "";
      try {
        return new Date(dateString).toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit"
        });
      } catch {
        return "";
      }
    },

    /** Returns relative time string (e.g. "5m ago", "2h ago", "3d ago") or "" for older. */
    getTimeAgo(dateString) {
      if (!dateString) return "";
      try {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);
        
        if (diffMins < 60) {
          return `${diffMins}m ago`;
        } else if (diffHours < 24) {
          return `${diffHours}h ago`;
        } else if (diffDays < 7) {
          return `${diffDays}d ago`;
        } else {
          return "";
        }
      } catch {
        return "";
      }
    },

    /** Truncates address to 25 chars with "..." if longer. */
    truncateAddress(address) {
      if (!address) return "";
      if (address.length <= 25) return address;
      return address.substring(0, 22) + '...';
    },

    /** Formats number with en-KE locale (e.g. 1,234.56). */
    formatNumber(number) {
      return new Intl.NumberFormat("en-KE").format(number);
    },

    /** Maps status key to display label (Pending, Processing, etc.). */
    formatStatusText(status) {
      const map = {
        pending: "Pending",
        processing: "Processing",
        completed: "Completed",
        cancelled: "Cancelled"
      };
      return map[status] || status;
    },

    /** Returns Font Awesome icon class for order status. */
    statusIcon(status) {
      const map = {
        pending: "fa-solid fa-clock",
        processing: "fa-solid fa-truck-fast",
        completed: "fa-solid fa-circle-check",
        cancelled: "fa-solid fa-ban"
      };
      return map[status] || "fa-solid fa-circle";
    },

    /**
     * Validates if a URL is a safe Google Maps URL
     * Only allows URLs from google.com/maps domains
     * @param {string} url - URL to validate
     * @returns {boolean} - True if valid Google Maps URL
     */
    isValidGoogleMapsUrl(url) {
      if (!url || typeof url !== 'string') return false;
      
      try {
        const urlObj = new URL(url.trim());
        const hostname = urlObj.hostname.toLowerCase();
        
        // Only allow google.com and maps.google.com domains
        const allowedDomains = [
          'maps.google.com',
          'www.google.com',
          'google.com',
          'maps.app.goo.gl', // Google Maps short URLs
          'goo.gl' // Legacy Google short URLs (if still in use)
        ];
        
        // Check if hostname matches allowed domains
        const isAllowedDomain = allowedDomains.some(domain => 
          hostname === domain || hostname.endsWith('.' + domain)
        );
        
        if (!isAllowedDomain) return false;
        
        // Additional check: ensure it's HTTPS (secure)
        if (urlObj.protocol !== 'https:') return false;
        
        // Check for common Google Maps URL patterns
        const pathname = urlObj.pathname.toLowerCase();
        const isMapsPath = pathname.includes('/maps') || 
                          pathname.includes('/place') ||
                          pathname.startsWith('/@') || // Coordinates format
                          urlObj.searchParams.has('q') || // Query parameter
                          urlObj.searchParams.has('ll'); // Lat/long parameter
        
        return isMapsPath || hostname.includes('maps');
      } catch (e) {
        // Invalid URL format
        return false;
      }
    },

    /**
     * Sanitizes URL to ensure it's safe to open
     * Adds rel="noopener noreferrer" protection
     * @param {string} url - URL to sanitize
     * @returns {string} - Sanitized URL
     */
    sanitizeUrl(url) {
      if (!url || typeof url !== 'string') return '#';
      
      try {
        const urlObj = new URL(url.trim());
        // Ensure HTTPS
        urlObj.protocol = 'https:';
        return urlObj.toString();
      } catch (e) {
        return '#';
      }
    },

    /**
     * Safely opens PIN location URL with additional security checks
     * @param {string} url - URL to open
     */
    openPinLocation(url) {
      if (!this.isValidGoogleMapsUrl(url)) {
        this.showToastMessage("Invalid link", "Only Google Maps URLs are allowed.", "error");
        return;
      }
      const sanitized = this.sanitizeUrl(url);
      window.open(sanitized, '_blank', 'noopener,noreferrer');
    },

  },

  mounted() {
    if (this.canManageOrders) {
      this.fetchOrders();
    }
  },
};
</script>

<style scoped>
/*
  Layout: .orders-wrapper (full width, fluid padding).
  Table: .table-scroll + .expanded-table (horizontal scroll on small screens).
  Modals: .modal-overlay + .modal-box (order detail, confirm cancel).
  Toast: fixed bottom-right, success/error/info variants.
  Breakpoints: 1024px, 768px, 480px, 360px.
*/
/* Base styles */
.orders-wrapper {
  padding: clamp(12px, 2.5vw, 20px);
  background: var(--background-color1);
  min-height: calc(100vh - 60px);
  max-width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
}

/* Access denied (admin role not super_admin or support) */
.access-denied-card {
  max-width: 420px;
  margin: 60px auto;
  padding: 32px;
  background: var(--background-color2);
  border-radius: 12px;
  border: 1px solid var(--border-color2);
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}
.access-denied-icon {
  font-size: 48px;
  color: #F59E0B;
  margin-bottom: 16px;
}
.access-denied-card h3 {
  font-size: 1.25rem;
  color: var(--text-color1);
  margin: 0 0 12px 0;
}
.access-denied-card p {
  font-size: 0.9375rem;
  color: var(--text-color2);
  margin: 0 0 8px 0;
  line-height: 1.5;
}
.access-denied-hint {
  font-size: 0.8125rem;
  opacity: 0.85;
}

/* Header */
.orders-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: clamp(16px, 3vw, 24px);
  flex-wrap: wrap;
  gap: clamp(12px, 2vw, 16px);
}

.header-left .title {
  font-size: clamp(1.25rem, 2.5vw, 1.5rem);
  font-weight: 700;
  color: var(--text-color1);
  margin: 0;
}

.header-left .subtitle {
  font-size: clamp(0.8125rem, 1.5vw, 0.875rem);
  color: var(--text-color2);
  margin-top: 4px;
}

.actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.search-container, .filter-container {
  position: relative;
}

.search-icon, .filter-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-color2);
  font-size: 0.875rem;
}

.search-box {
  padding: 10px 12px 10px 36px;
  border: 1px solid var(--border-color2);
  border-radius: 8px;
  background: var(--background-color2);
  color: var(--text-color1);
  font-size: 0.875rem;
  width: 240px;
  transition: all 0.2s;
}

.search-box:focus {
  outline: none;
  border-color: var(--border-color-hover);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.filter-select {
  padding: 10px 12px 10px 36px;
  border: 1px solid var(--border-color2);
  border-radius: 8px;
  background: var(--background-color2);
  color: var(--text-color1);
  font-size: 0.875rem;
  width: 140px;
  cursor: pointer;
  appearance: none;
}

/* Expanded Table */
.table-container {
  background: var(--background-color2);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  width: 100%;
  overflow-x: auto;
}

.expanded-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  min-width: 1200px;
}

.expanded-table th {
  padding: 16px 12px;
  text-align: left;
  font-weight: 600;
  color: var(--text-color2);
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid var(--border-color2);
  background: rgba(0, 0, 0, 0.02);
  white-space: nowrap;
}

.expanded-table td {
  padding: 16px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  vertical-align: middle;
  height: 72px;
}

.table-row {
  transition: all 0.2s ease;
}

.table-row:hover {
  background: rgba(59, 130, 246, 0.04);
}

.table-row:last-child td {
  border-bottom: none;
}

/* Column widths */
.expanded-table th:nth-child(1),
.expanded-table td:nth-child(1) {
  width: 100px; /* Order ID */
}

.expanded-table th:nth-child(2),
.expanded-table td:nth-child(2) {
  width: 140px; /* Customer */
}

.expanded-table th:nth-child(3),
.expanded-table td:nth-child(3) {
  width: 120px; /* Contact */
}

.expanded-table th:nth-child(4),
.expanded-table td:nth-child(4) {
  width: 140px; /* Location */
}

.expanded-table th:nth-child(5),
.expanded-table td:nth-child(5) {
  width: 120px; /* Items */
}

.expanded-table th:nth-child(6),
.expanded-table td:nth-child(6) {
  width: 140px; /* Amount */
}

.expanded-table th:nth-child(7),
.expanded-table td:nth-child(7) {
  width: 130px; /* Status */
}

.expanded-table th:nth-child(8),
.expanded-table td:nth-child(8) {
  width: 140px; /* Date & Time */
}

.expanded-table th:nth-child(9),
.expanded-table td:nth-child(9) {
  width: 120px; /* Actions */
}

/* Order ID Column */
.order-id {
  font-weight: 600;
  color: var(--text-color1);
  font-size: 0.875rem;
}

/* Customer Column */
.customer-col .customer-name {
  font-weight: 600;
  color: var(--text-color1);
  font-size: 0.875rem;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.customer-contact {
  font-size: 0.75rem;
  color: var(--text-color2);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Contact Column */
.contact-col .phone {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
  color: var(--text-color1);
}

.contact-icon {
  color: var(--border-color-hover);
  font-size: 0.75rem;
}

/* Location Column */
.location-col .city {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
  color: var(--text-color1);
  margin-bottom: 4px;
}

.location-icon {
  color: #10B981;
  font-size: 0.75rem;
}

.area {
  font-size: 0.75rem;
  color: var(--text-color2);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Items Column */
.items-col .items-count {
  font-weight: 600;
  color: var(--text-color1);
  font-size: 0.875rem;
  margin-bottom: 4px;
}

.items-total {
  font-size: 0.75rem;
  color: var(--text-color2);
}

.discount-indicator {
  font-size: 0.7rem;
  color: #28a745;
  background: rgba(40, 167, 69, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-top: 4px;
}

.discount-indicator i {
  font-size: 0.6rem;
}

/* Amount Column */
.amount-col .total-amount {
  font-weight: 700;
  color: var(--text-color1);
  font-size: 0.875rem;
  margin-bottom: 4px;
}

.savings-indicator {
  font-size: 0.7rem;
  color: #28a745;
  font-weight: 600;
  margin-bottom: 2px;
}

.delivery-fee {
  font-size: 0.7rem;
  color: #10B981;
  font-weight: 500;
}

/* Status Column */
.status-wrapper {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
}

.status-badge.pending {
  background: #FEF3C7;
  color: #92400E;
}

.status-badge.processing {
  background: #DBEAFE;
  color: #1E40AF;
}

.status-badge.completed {
  background: #D1FAE5;
  color: #065F46;
}

.status-badge.cancelled {
  background: #FEE2E2;
  color: #991B1B;
}

.status-time {
  font-size: 0.7rem;
  color: var(--text-color2);
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Date & Time Column */
.datetime-col .date {
  font-weight: 600;
  color: var(--text-color1);
  font-size: 0.875rem;
  margin-bottom: 2px;
}

.datetime-col .time {
  font-size: 0.75rem;
  color: var(--text-color2);
  margin-bottom: 2px;
}

.datetime-col .time-ago {
  font-size: 0.7rem;
  color: var(--text-color2);
  font-style: italic;
}

/* Actions Column */
.actions-col .action-buttons {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.view-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  background: var(--border-color-hover);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
}

.view-btn:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

.quick-actions {
  display: flex;
  gap: 4px;
  justify-content: center;
}

.quick-btn {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.7rem;
  transition: all 0.2s;
}

.quick-btn.process {
  background: #3B82F6;
  color: white;
}

.quick-btn.complete {
  background: #10B981;
  color: white;
}

.quick-btn.cancel {
  background: #EF4444;
  color: white;
}

.quick-btn:hover {
  opacity: 0.9;
  transform: scale(1.05);
}

/* Confirm cancel modal */
.confirm-modal {
  max-width: 420px;
}

.confirm-modal .modal-body {
  padding: 16px 0 0;
}

.confirm-modal .modal-body p {
  margin: 0 0 20px;
  color: var(--text-color1);
  font-size: 0.9375rem;
  line-height: 1.5;
}

.modal-actions-inline {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.cancel-confirm-btn {
  width: auto;
  padding: 8px 14px;
  font-size: 0.8rem;
}

/* Toast */
.toast {
  position: fixed;
  bottom: 24px;
  right: 24px;
  left: 24px;
  max-width: 380px;
  margin-left: auto;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 10px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  z-index: 10000;
  animation: toast-in 0.3s ease;
}

@keyframes toast-in {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.toast.success {
  background: #D1FAE5;
  color: #065F46;
  border-left: 4px solid #10B981;
}

.toast.error {
  background: #FEE2E2;
  color: #991B1B;
  border-left: 4px solid #EF4444;
}

.toast.info {
  background: #DBEAFE;
  color: #1E40AF;
  border-left: 4px solid #3B82F6;
}

.toast i:first-child {
  font-size: 1.25rem;
  flex-shrink: 0;
  margin-top: 2px;
}

.toast-content {
  flex: 1;
  min-width: 0;
}

.toast-content strong {
  display: block;
  font-size: 0.875rem;
  margin-bottom: 2px;
}

.toast-content p {
  margin: 0;
  font-size: 0.8125rem;
  opacity: 0.95;
}

.toast-close {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  opacity: 0.7;
  color: inherit;
  flex-shrink: 0;
}

.toast-close:hover {
  opacity: 1;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(59, 130, 246, 0.1);
  border-radius: 50%;
  border-top-color: #3B82F6;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-state p {
  color: var(--text-color2);
  font-size: 0.875rem;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 80px 20px;
}

.empty-icon {
  font-size: 48px;
  color: var(--border-color-hover);
  opacity: 0.5;
  margin-bottom: 16px;
}

.empty-state h3 {
  font-size: 1.125rem;
  color: var(--text-color1);
  margin-bottom: 8px;
}

.empty-state p {
  color: var(--text-color2);
  font-size: 0.875rem;
  max-width: 400px;
  margin: 0 auto;
}

/* Discount specific styles */
.summary-item.discount {
  color: #28a745;
  background: rgba(40, 167, 69, 0.05);
  padding: 8px 12px;
  border-radius: 6px;
  margin: 4px 0;
}

.discount-amount {
  color: #28a745;
  font-weight: 600;
}

.summary-item.after-discount {
  font-weight: 600;
  background: rgba(76, 175, 80, 0.1);
  padding: 8px 12px;
  border-radius: 6px;
  margin: 8px 0;
}

.savings-message {
  margin-top: 12px;
  padding: 10px;
  background: rgba(40, 167, 69, 0.1);
  border-radius: 8px;
  border-left: 4px solid #28a745;
  font-size: 0.875rem;
  color: #28a745;
  display: flex;
  align-items: center;
  gap: 8px;
}

.discount-badge-header {
  background: linear-gradient(135deg, #28a745, #20c997);
  color: white;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 600;
  margin-left: 8px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.price-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 4px;
}

.original-price {
  font-size: 0.7rem;
  color: #888;
  text-decoration: line-through;
}

.discounted-price {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-color1);
}

.discount-badge-item {
  background: #ff4444;
  color: white;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 0.6rem;
  font-weight: 600;
}

.regular-price .item-price {
  font-size: 0.8rem;
  color: var(--text-color1);
}

.original-total {
  margin-top: 2px;
}

.original-total-crossed {
  font-size: 0.7rem;
  color: #888;
  text-decoration: line-through;
}

.savings-summary {
  margin-top: 16px;
  padding: 12px;
  background: rgba(40, 167, 69, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(40, 167, 69, 0.2);
}

.savings-row {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  font-size: 0.8rem;
}

.savings-row.discount {
  color: #28a745;
  font-weight: 600;
}

.savings-row.final {
  border-top: 1px solid rgba(0,0,0,0.1);
  padding-top: 8px;
  margin-top: 8px;
  font-weight: 600;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.modal-box {
  background: var(--background-color2);
  border-radius: 16px;
  width: 100%;
  max-width: 1000px;
  max-height: 90vh;
  overflow-y: auto;
  animation: slide-up 0.3s ease;
}

@keyframes slide-up {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 24px;
  border-bottom: 1px solid var(--border-color2);
}

.modal-title-section h2 {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text-color1);
  margin: 0 0 8px 0;
}

.order-meta {
  display: flex;
  gap: 16px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: var(--text-color2);
}

.close-modal-btn {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid var(--border-color2);
  background: var(--background-color1);
  color: var(--text-color1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.close-modal-btn:hover {
  background: var(--border-color2);
}

.status-banner {
  padding: 16px 24px;
  margin: 0 24px 24px;
  border-radius: 8px;
}

.status-banner.pending {
  background: #FEF3C7;
  border-left: 4px solid #F59E0B;
}

.status-banner.processing {
  background: #DBEAFE;
  border-left: 4px solid #3B82F6;
}

.status-banner.completed {
  background: #D1FAE5;
  border-left: 4px solid #10B981;
}

.status-banner.cancelled {
  background: #FEE2E2;
  border-left: 4px solid #EF4444;
}

.status-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.status-content i {
  font-size: 1.25rem;
}

.status-content h3 {
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 4px 0;
}

.status-content p {
  font-size: 0.875rem;
  margin: 0;
  opacity: 0.8;
}

.modal-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  padding: 0 24px 24px;
}

.modal-left, .modal-right {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.info-card, .items-card, .action-buttons-card {
  background: var(--background-color1);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid var(--border-color2);
}

.info-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-color1);
  margin: 0 0 16px 0;
}

.info-title i {
  color: var(--border-color-hover);
}

.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-item.full-width {
  grid-column: 1 / -1;
}

.info-label {
  font-size: 0.75rem;
  color: var(--text-color2);
}

.info-value {
  font-size: 0.875rem;
  color: var(--text-color1);
  font-weight: 500;
}

.pin-location-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #4285F4;
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 500;
  padding: 6px 12px;
  background: rgba(66, 133, 244, 0.1);
  border-radius: 6px;
  transition: all 0.2s;
}

.pin-location-link:hover {
  background: rgba(66, 133, 244, 0.2);
  text-decoration: underline;
}

.pin-location-link i {
  font-size: 0.75rem;
}

.warning-text {
  color: #F59E0B;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.warning-text i {
  font-size: 0.75rem;
}

/* Updated summary item styles */
.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  font-size: 0.875rem;
  color: var(--text-color1);
  border-bottom: 1px dashed var(--border-color2);
}

.summary-item:last-child {
  border-bottom: none;
}

.summary-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  font-size: 1rem;
  color: var(--text-color1);
  border-top: 2px solid var(--border-color2);
  margin-top: 8px;
}

.payment-method {
  display: flex;
  align-items: center;
  gap: 6px;
}

.items-list {
  max-height: 300px;
  overflow-y: auto;
}

.item-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid var(--border-color2);
}

.item-thumb-wrap {
  flex-shrink: 0;
}

.item-thumb {
  width: 56px;
  height: 56px;
  border-radius: 8px;
  object-fit: cover;
  background: var(--background-color1);
  border: 1px solid var(--border-color2);
}

.item-info {
  flex: 1;
  min-width: 0;
}

.item-row:last-child {
  border-bottom: none;
}

.item-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-color1);
  margin-bottom: 4px;
}

.item-color-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: var(--text-color2);
  margin-bottom: 4px;
}

.item-color-swatch {
  display: inline-flex;
  width: 14px;
  height: 14px;
  border-radius: 3px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
}

.item-color-swatch__half {
  display: block;
  width: 50%;
  height: 100%;
}

.item-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.75rem;
  color: var(--text-color2);
}

.item-size-line {
  color: #2563eb;
  font-weight: 600;
}

.item-total {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-color1);
  min-width: 80px;
  text-align: right;
}

.action-buttons-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
}

.action-btn.process {
  background: #3B82F6;
  color: white;
}

.action-btn.complete {
  background: #10B981;
  color: white;
}

.action-btn.cancel {
  background: #EF4444;
  color: white;
}

.action-btn.secondary {
  background: var(--background-color2);
  color: var(--text-color1);
  border: 1px solid var(--border-color2);
}

.action-btn.disabled {
  background: var(--border-color2);
  color: var(--text-color2);
  cursor: not-allowed;
  opacity: 0.7;
}

.action-btn:not(.disabled):hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

/* Table scroll wrapper for small screens */
.table-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin: 0 calc(-1 * clamp(12px, 2.5vw, 20px));
  padding: 0 clamp(12px, 2.5vw, 20px);
}

/* Responsive */
@media (max-width: 1024px) {
  .modal-content {
    grid-template-columns: 1fr;
  }
  
  .expanded-table {
    min-width: 1000px;
  }
  
  .table-scroll {
    margin-left: calc(-1 * clamp(12px, 2.5vw, 20px));
    margin-right: calc(-1 * clamp(12px, 2.5vw, 20px));
    padding-left: clamp(12px, 2.5vw, 20px);
    padding-right: clamp(12px, 2.5vw, 20px);
  }
}

@media (max-width: 768px) {
  .orders-header {
    flex-direction: column;
    align-items: stretch;
  }
  
  .header-left .title {
    font-size: 1.25rem;
  }
  
  .actions {
    flex-direction: column;
    gap: 10px;
  }
  
  .search-container,
  .filter-container {
    width: 100%;
  }
  
  .search-box,
  .filter-select {
    width: 100%;
    min-height: 44px;
  }
  
  .expanded-table {
    min-width: 880px;
    font-size: 0.8125rem;
  }
  
  .expanded-table th,
  .expanded-table td {
    padding: 10px 8px;
  }
  
  .modal-box {
    max-width: 96vw;
    margin: 12px;
    max-height: calc(100vh - 24px);
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  
  .modal-content {
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    flex: 1;
    min-height: 0;
  }
  
  .modal-header {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
  
  .close-modal-btn {
    align-self: flex-end;
    min-width: 44px;
    min-height: 44px;
  }
  
  .action-buttons-grid {
    grid-template-columns: 1fr;
  }
  
  .action-btn {
    min-height: 44px;
  }
  
  .toast {
    left: 12px;
    right: 12px;
    bottom: 12px;
    max-width: none;
  }
}

@media (max-width: 480px) {
  .orders-wrapper {
    padding: 12px;
  }
  
  .table-scroll {
    margin-left: -12px;
    margin-right: -12px;
    padding-left: 12px;
    padding-right: 12px;
  }
  
  .expanded-table {
    min-width: 760px;
  }
  
  .modal-box {
    margin: 8px;
    padding: 14px;
    max-height: calc(100vh - 16px);
  }
  
  .modal-content {
    padding: 0;
  }
  
  .info-grid {
    grid-template-columns: 1fr;
  }
  
  .info-card {
    padding: 12px;
  }
  
  .confirm-modal .modal-body .modal-actions-inline {
    flex-direction: column;
  }
  
  .confirm-modal .modal-body .modal-actions-inline .view-btn,
  .confirm-modal .modal-body .modal-actions-inline .cancel-confirm-btn {
    width: 100%;
    min-height: 44px;
  }
  
  .toast {
    left: 8px;
    right: 8px;
    bottom: 8px;
    padding: 12px 14px;
  }
  
  .toast-content strong {
    font-size: 0.8125rem;
  }
  
  .toast-content p {
    font-size: 0.75rem;
  }
}

@media (max-width: 360px) {
  .orders-wrapper {
    padding: 8px;
  }
  
  .header-left .title {
    font-size: 1.125rem;
  }
  
  .view-btn,
  .quick-btn {
    min-height: 44px;
  }
  
  .status-badge {
    padding: 8px 10px;
    font-size: 0.7rem;
  }
}

/* Transitions */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active, .slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from, .slide-up-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
</style>